import type { FC } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'

type Props = {
  /** What the user wanted and did not get, in one line. */
  title: string
  /** The reason, as the Gateway reported it. */
  detail?: string
  tone?: 'error' | 'info'
  onRetry?: () => void
  retryLabel?: string
}

/**
 * The one place that says "this did not load, and here is why". An empty table
 * or a row of "-" reads as "no data", which hides a failed request.
 */
const PageStateMessage: FC<Props> = ({
  title,
  detail,
  tone = 'info',
  onRetry,
  retryLabel = 'נסו שוב',
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const RUBIK =
    '"Rubik", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
  const titleColor =
    tone === 'error'
      ? isDarkMode
        ? '#FCA5A5'
        : '#C0392B'
      : isDarkMode
        ? '#FFFFFF'
        : '#003F61'

  return (
    <Box
      dir="rtl"
      role={tone === 'error' ? 'alert' : 'status'}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        py: 4,
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{
          fontFamily: RUBIK,
          fontSize: 16,
          fontWeight: 400,
          color: titleColor,
        }}
      >
        {title}
      </Typography>
      {detail && (
        <Typography
          sx={{
            fontFamily: RUBIK,
            fontSize: 14,
            fontWeight: 300,
            maxWidth: 640,
            color: isDarkMode ? '#9CA3AF' : '#6B7280',
          }}
        >
          {detail}
        </Typography>
      )}
      {onRetry && (
        <Button
          variant="outlined"
          onClick={onRetry}
          sx={{
            mt: 1,
            borderColor: isDarkMode ? '#4B5563' : '#E4E7EB',
            color: isDarkMode ? '#FFFFFF' : '#003F61',
            borderRadius: 1,
            px: 3,
            fontFamily: RUBIK,
            fontSize: 14,
            '&:hover': { bgcolor: isDarkMode ? '#374151' : '#F5F5F5' },
          }}
        >
          {retryLabel}
        </Button>
      )}
    </Box>
  )
}

export default PageStateMessage
